import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Users } from "lucide-react";
import { MapLocation } from "@/types/location";
import { useLanguage } from "@/contexts/LanguageContext";

interface RegionSelectorProps {
  activeRegion: 'all' | 'Riyadh' | 'Jeddah' | 'Eastern' | 'Medina' | 'Al Kharj' | 'Dawadmi' | 'Tabuk' | 'Hail' | 'Hafar Al Batin' | 'Majmaa' | 'Qassim' | 'Sakaka' | 'Arar' | 'Al Qurayyat' | 'Al Jubail' | 'Yanbu' | 'Khamis Mushait' | 'Sabia' | 'Abha' | 'Jizan' | 'Najran' | 'Muhayil' | 'Makkah' | 'Al Bahah';
  activeMediaOwner: string;
  activeMediaKind: string;
  onRegionChange: (region: 'all' | 'Riyadh' | 'Jeddah' | 'Eastern' | 'Medina' | 'Al Kharj' | 'Dawadmi' | 'Tabuk' | 'Hail' | 'Hafar Al Batin' | 'Majmaa' | 'Qassim' | 'Sakaka' | 'Arar' | 'Al Qurayyat' | 'Al Jubail' | 'Yanbu' | 'Khamis Mushait' | 'Sabia' | 'Abha' | 'Jizan' | 'Najran' | 'Muhayil' | 'Makkah' | 'Al Bahah') => void;
  onToggleAll: (region: string) => void;
  onToggleType: (region: string, type: string) => void;
  getLocationTypes: () => string[];
  selectedLocations: Set<number>;
  locations: MapLocation[];
}

export const RegionSelector = ({
  activeRegion,
  activeMediaOwner,
  activeMediaKind,
  onRegionChange,
  onToggleAll,
  onToggleType,
  getLocationTypes,
  selectedLocations,
  locations
}: RegionSelectorProps) => {
  const { t } = useLanguage();

  const getRegionName = (regionId: string) => {
    const regionMap: Record<string, string> = {
      'all': t('allRegions') || 'All Regions',
      'Riyadh': t('riyadh') || 'Riyadh',
      'Jeddah': t('jeddah') || 'Jeddah',
      'Eastern': t('easternProvince') || 'Eastern Province',
      'Medina': t('medina') || 'Medina',
      'Al Kharj': t('alKharj') || 'Al Kharj',
      'Dawadmi': t('dawadmi') || 'Dawadmi',
      'Tabuk': t('tabuk') || 'Tabuk',
      'Hail': t('hail') || 'Hail',
      'Hafar Al Batin': t('hafarAlBatin') || 'Hafar Al Batin',
      'Majmaa': t('majmaa') || 'Majmaa',
      'Qassim': t('qassim') || 'Qassim',
      'Sakaka': t('sakaka') || 'Sakaka',
      'Arar': t('arar') || 'Arar',
      'Al Qurayyat': t('alQurayyat') || 'Al Qurayyat',
      'Al Jubail': t('alJubail') || 'Al Jubail',
      'Yanbu': t('yanbu') || 'Yanbu',
      'Khamis Mushait': t('khamisMushait') || 'Khamis Mushait',
      'Sabia': t('sabia') || 'Sabia',
      'Abha': t('abha') || 'Abha',
      'Jizan': t('jizan') || 'Jizan',
      'Najran': t('najran') || 'Najran',
      'Muhayil': t('muhayil') || 'Muhayil',
      'Al Bahah': t('alBahah') || 'Al Bahah'
    };
    return regionMap[regionId] || regionId;
  };

  const regions: Array<{ id: 'all' | 'Riyadh' | 'Jeddah' | 'Eastern' | 'Medina' | 'Al Kharj' | 'Dawadmi' | 'Tabuk' | 'Hail' | 'Hafar Al Batin' | 'Majmaa' | 'Qassim' | 'Sakaka' | 'Arar' | 'Al Qurayyat' | 'Al Jubail' | 'Yanbu' | 'Khamis Mushait' | 'Sabia' | 'Abha' | 'Jizan' | 'Najran' | 'Muhayil' | 'Makkah' | 'Al Bahah'; name: string }> = [
    { id: 'all', name: getRegionName('all') },
    { id: 'Riyadh', name: getRegionName('Riyadh') },
    { id: 'Jeddah', name: getRegionName('Jeddah') },
    { id: 'Makkah' as any, name: 'Makkah' },
    { id: 'Eastern', name: getRegionName('Eastern') },
    { id: 'Medina', name: getRegionName('Medina') },
    { id: 'Al Kharj', name: getRegionName('Al Kharj') },
    { id: 'Dawadmi', name: getRegionName('Dawadmi') },
    { id: 'Tabuk', name: getRegionName('Tabuk') },
    { id: 'Hail', name: getRegionName('Hail') },
    { id: 'Hafar Al Batin', name: getRegionName('Hafar Al Batin') },
    { id: 'Majmaa', name: getRegionName('Majmaa') },
    { id: 'Qassim', name: getRegionName('Qassim') },
    { id: 'Sakaka', name: getRegionName('Sakaka') },
    { id: 'Arar', name: getRegionName('Arar') },
    { id: 'Al Qurayyat', name: getRegionName('Al Qurayyat') },
    { id: 'Al Jubail', name: getRegionName('Al Jubail') },
    { id: 'Yanbu', name: getRegionName('Yanbu') },
    { id: 'Khamis Mushait', name: getRegionName('Khamis Mushait') },
    { id: 'Sabia', name: getRegionName('Sabia') },
    { id: 'Abha', name: getRegionName('Abha') },
    { id: 'Jizan', name: getRegionName('Jizan') },
    { id: 'Najran', name: getRegionName('Najran') },
    { id: 'Muhayil', name: getRegionName('Muhayil') },
    { id: 'Al Bahah', name: getRegionName('Al Bahah') }
  ];

  const handleRegionClick = (regionId: typeof regions[0]['id']) => {
    console.log(`RegionSelector: Changing to region: ${regionId}`);
    onRegionChange(regionId);
  };

  const getRegionLocationCount = (region: string) => {
    if (region === 'all') {
      return locations.filter(loc => {
        const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
        const mediaKindMatch = activeMediaKind === 'all' || 
          loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
        return mediaOwnerMatch && mediaKindMatch;
      }).length;
    }
    return locations.filter(loc => {
      const regionMatch = loc.region === region;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    }).length;
  };

  const getSelectedLocationCount = (region: string) => {
    if (region === 'all') {
      return Array.from(selectedLocations).filter(id => {
        const loc = locations.find(l => l.id === id);
        if (!loc) return false;
        const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
        const mediaKindMatch = activeMediaKind === 'all' || 
          loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
        return mediaOwnerMatch && mediaKindMatch;
      }).length;
    }
    return Array.from(selectedLocations).filter(id => {
      const loc = locations.find(l => l.id === id);
      if (!loc) return false;
      const regionMatch = loc.region === region;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    }).length;
  };

  const getLocationTypesForRegion = (region: string) => {
    const filteredLocs = locations.filter(loc => {
      const regionMatch = region === 'all' ? true : loc.region === region;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    });
    return [...new Set(filteredLocs.map(loc => loc.type))];
  };

  // Filter regions to only show those with locations
  const visibleRegions = regions.filter(region => {
    const totalCount = getRegionLocationCount(region.id);
    return totalCount > 0;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {visibleRegions.map((region) => {
          const totalCount = getRegionLocationCount(region.id);
          const selectedCount = getSelectedLocationCount(region.id);
          
          return (
            <Button
              key={region.id}
              variant={activeRegion === region.id ? "default" : "outline"}
              onClick={() => handleRegionClick(region.id)}
              className={`flex items-center gap-2 ${
                activeRegion === region.id 
                  ? 'bg-primary text-white' 
                  : 'bg-card dark:bg-card hover:bg-accent'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span>{region.name}</span>
              <Badge variant="secondary" className="ml-1">
                {selectedCount}/{totalCount}
              </Badge>
            </Button>
          );
        })}
      </div>

    </div>
  );
};
